import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileUploader } from "@/shared/components";
import { DatePicker, Input, Select, Toast } from "@/shared/ui";
import { useDocumentSuggest } from "@/shared/hooks";
import { applyOcrFields, parseOcrDate, type Locale } from "@/shared/utils";
import type { IdDocumentsSection as IdDocumentsSectionType } from "../../../types/personalSheet";
import { createEditIdDocumentsSchema, type EditIdDocumentsFullValues } from "./validation";
import type { SectionFormHandle } from "./types";

interface Props {
  idDocuments: IdDocumentsSectionType | null;
  gender: string;
  isResident: boolean;
  draftValues?: IdDocumentsSectionType | null;
}

const NO_PRIOR_TRAINING_VALUES = ["не военнообязанный", "призывник"];

function parseDate(dateStr: string | null): Date | null {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(date: Date | null): string {
  if (!date) return "";
  return date.toISOString().slice(0, 10);
}

function mapIdDocumentsToEditValues(idDocuments: IdDocumentsSectionType | null): EditIdDocumentsFullValues {
  return {
    national_id_number: idDocuments?.national_id_number ?? "",
    national_id_issue_date: idDocuments?.national_id_issue_date ?? "",
    national_id_expiry_date: idDocuments?.national_id_expiry_date ?? "",
    national_id_issued_by: idDocuments?.national_id_issued_by ?? "",
    military_obligation_relation: idDocuments?.military_obligation_relation ?? "",
    military_rank: idDocuments?.military_rank ?? "",
    military_composition: idDocuments?.military_composition ?? "",
    military_branch: idDocuments?.military_branch ?? "",
  };
}

const EditIdDocumentsSection = forwardRef<SectionFormHandle, Props>(function EditIdDocumentsSection(
  { idDocuments, gender, isResident, draftValues },
  ref,
) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const locale = (i18n.language as Locale) || "ru";
  const isMale = gender === "male";
  const docType = isResident ? "national_id" : "passport";

  const initialValues = useRef<EditIdDocumentsFullValues>(mapIdDocumentsToEditValues(idDocuments));
  const displayIdDocuments = draftValues ?? idDocuments;

  const [idFile, setIdFile] = useState<File | null>(null);
  const [existingIdFileRemoved, setExistingIdFileRemoved] = useState(false);
  const [militaryFile, setMilitaryFile] = useState<File | null>(null);
  const [existingMilitaryFileRemoved, setExistingMilitaryFileRemoved] = useState(false);
  const [militaryFileError, setMilitaryFileError] = useState<string | null>(null);
  const [ocrFilled, setOcrFilled] = useState<Set<string>>(new Set());
  const [ocrToast, setOcrToast] = useState(false);
  const [ocrWarning, setOcrWarning] = useState(false);
  const { scan: scanDocument, state: ocrState, reset: resetOcr } = useDocumentSuggest();

  const {
    register,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<EditIdDocumentsFullValues>({
    resolver: zodResolver(createEditIdDocumentsSchema(gender)),
    mode: "onTouched",
    defaultValues: mapIdDocumentsToEditValues(displayIdDocuments),
  });

  const issueDate = watch("national_id_issue_date");
  const expiryDate = watch("national_id_expiry_date");
  const militaryRelation = watch("military_obligation_relation");
  const isExempt = NO_PRIOR_TRAINING_VALUES.includes(militaryRelation ?? "");

  function applyOcrDateField(field: "national_id_issue_date" | "national_id_expiry_date", fields: Record<string, string>): boolean {
    if ((getValues(field) ?? "").trim() !== "") return false;
    const parsed = parseOcrDate(fields[field]);
    if (!parsed) return false;
    setValue(field, formatDate(parsed), { shouldValidate: true });
    return true;
  }

  async function runIdOcr(file: File) {
    setOcrToast(false);
    setOcrWarning(false);
    const result = await scanDocument(file, docType);
    if (result.status === "idle") return;
    const fields = result.fields ?? {};

    const matched =
      (fields.national_id_number?.trim() ? 1 : 0) +
      (fields.national_id_issued_by?.trim() ? 1 : 0) +
      (parseOcrDate(fields.national_id_issue_date) ? 1 : 0) +
      (parseOcrDate(fields.national_id_expiry_date) ? 1 : 0);
    if (matched * 2 < 4) {
      setOcrWarning(true);
      return;
    }

    const filled: string[] = [];

    const textResult = applyOcrFields(
      {
        national_id_number: getValues("national_id_number") ?? "",
        national_id_issued_by: getValues("national_id_issued_by") ?? "",
      },
      fields,
      ["national_id_number", "national_id_issued_by"],
    );
    for (const key of ["national_id_number", "national_id_issued_by"] as const) {
      if (textResult.filled.includes(key)) {
        setValue(key, textResult.next[key], { shouldValidate: true });
        filled.push(key);
      }
    }

    if (applyOcrDateField("national_id_issue_date", fields)) filled.push("national_id_issue_date");
    if (applyOcrDateField("national_id_expiry_date", fields)) filled.push("national_id_expiry_date");

    if (filled.length > 0) {
      setOcrFilled(new Set(filled));
      setOcrToast(true);
    }
  }

  function handleIdFileChange(file: File | null) {
    setIdFile(file);
    if (!file) {
      resetOcr();
      setOcrFilled(new Set());
      setOcrToast(false);
      setOcrWarning(false);
      return;
    }
    void runIdOcr(file);
  }

  const obligationRelationOptions = [
    { label: t("personalSheet.militaryObligationOptions.conscript"), value: "призывник" },
    { label: t("personalSheet.militaryObligationOptions.military"), value: "военнообязанный" },
    { label: t("personalSheet.militaryObligationOptions.exempt"), value: "не военнообязанный" },
  ];

  const MILITARY_FIELDS: (keyof EditIdDocumentsFullValues)[] = [
    "military_obligation_relation",
    "military_rank",
    "military_composition",
    "military_branch",
  ];

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        const current = getValues();
        const initial = initialValues.current;
        const changes: Record<string, unknown> = {};
        const fields: (keyof EditIdDocumentsFullValues)[] = [
          "national_id_number",
          "national_id_issue_date",
          "national_id_expiry_date",
          "national_id_issued_by",
          ...MILITARY_FIELDS,
        ];
        for (const field of fields) {
          if (current[field] !== initial[field]) {
            changes[field] = current[field];
          }
        }
        return Object.keys(changes).length > 0 ? changes : null;
      },
      getFiles() {
        const files: Record<string, File> = {};
        if (idFile) files["id_documents.national_id_file"] = idFile;
        if (militaryFile) files["id_documents.military_certificate_file"] = militaryFile;
        return Object.keys(files).length > 0 ? files : null;
      },
      async validate() {
        const valid = await trigger();
        const hasMilitaryFile = militaryFile !== null || (!existingMilitaryFileRemoved && !!displayIdDocuments?.military_certificate_file_url);
        const fileOk = !(isMale && !isExempt) || hasMilitaryFile;
        setMilitaryFileError(fileOk ? null : "personalSheet.validation.required");
        return valid && fileOk;
      },
      isDirty() {
        const current = getValues();
        const initial = initialValues.current;
        const fieldChanged = [
          "national_id_number",
          "national_id_issue_date",
          "national_id_expiry_date",
          "national_id_issued_by",
          ...MILITARY_FIELDS,
        ].some((field) => current[field as keyof EditIdDocumentsFullValues] !== initial[field as keyof EditIdDocumentsFullValues]);
        return fieldChanged || idFile !== null || militaryFile !== null;
      },
    };
  });

  return (
    <div className="flex flex-col gap-4">
      {ocrState === "scanning" && (
        <Toast color="grey" text={t("personalSheet.ocr.scanning")} closable={false} autoClose={false} isFullWidth />
      )}
      {ocrToast && (
        <Toast
          key="id-ocr-filled"
          color="positive"
          text={t("personalSheet.ocr.filled")}
          isFullWidth
          onClose={() => setOcrToast(false)}
        />
      )}
      {ocrWarning && (
        <Toast
          key="id-ocr-warning"
          color="notice"
          text={t(`personalSheet.ocr.wrongFile.${docType}`)}
          isFullWidth
          onClose={() => setOcrWarning(false)}
        />
      )}

      <Input
        label={t("personalSheet.fields.nationalIdNumber")}
        placeholder={t("personalSheet.fields.nationalIdNumber")}
        hasRequiredLabel
        highlight={ocrFilled.has("national_id_number")}
        {...register("national_id_number")}
        error={errors.national_id_number?.message ? t(errors.national_id_number.message) : undefined}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DatePicker
          mode="single"
          label={t("personalSheet.fields.nationalIdIssueDate")}
          placeholder={t("personalSheet.fields.nationalIdIssueDate")}
          hasRequiredLabel
          locale={locale}
          highlight={ocrFilled.has("national_id_issue_date")}
          value={parseDate(issueDate)}
          onChange={(date) => setValue("national_id_issue_date", formatDate(date as Date | null), { shouldValidate: true })}
          error={errors.national_id_issue_date?.message ? t(errors.national_id_issue_date.message) : undefined}
        />

        <DatePicker
          mode="single"
          label={t("personalSheet.fields.nationalIdExpiryDate")}
          placeholder={t("personalSheet.fields.nationalIdExpiryDate")}
          hasRequiredLabel
          locale={locale}
          highlight={ocrFilled.has("national_id_expiry_date")}
          value={parseDate(expiryDate)}
          onChange={(date) => setValue("national_id_expiry_date", formatDate(date as Date | null), { shouldValidate: true })}
          error={errors.national_id_expiry_date?.message ? t(errors.national_id_expiry_date.message) : undefined}
        />
      </div>

      <Input
        label={t("personalSheet.fields.nationalIdIssuedBy")}
        placeholder={t("personalSheet.fields.nationalIdIssuedBy")}
        hasRequiredLabel
        highlight={ocrFilled.has("national_id_issued_by")}
        {...register("national_id_issued_by")}
        error={errors.national_id_issued_by?.message ? t(errors.national_id_issued_by.message) : undefined}
      />

      <FileUploader
        label={t("personalSheet.fields.nationalIdFile")}
        hasRequiredLabel
        value={idFile}
        existingFileUrl={existingIdFileRemoved ? null : (displayIdDocuments?.national_id_file_url ?? null)}
        onChange={handleIdFileChange}
        onRemoveExisting={() => setExistingIdFileRemoved(true)}
        accept=".pdf,.jpg,.jpeg,.png"
      />

      <div className="pt-4 border-t surface-base-stroke flex flex-col gap-4">
        <div className="flex items-center gap-2">
          <h3 className="text-body-bold-md content-base-primary">{t("personalSheet.military.title")}</h3>
          {!isMale && <span className="text-label-sm content-base-secondary">{t("personalSheet.military.optionalHint")}</span>}
        </div>

        <Select
          label={t("personalSheet.fields.militaryObligationRelation")}
          options={obligationRelationOptions}
          value={militaryRelation || undefined}
          onChange={(v) => setValue("military_obligation_relation", v ?? "", { shouldValidate: true })}
          placeholder={t("personalSheet.fields.militaryObligationRelation")}
          hasRequiredLabel={isMale}
          error={errors.military_obligation_relation?.message ? t(errors.military_obligation_relation.message) : undefined}
        />

        <Input
          label={t("personalSheet.fields.militaryRank")}
          placeholder={t("personalSheet.fields.militaryRank")}
          disabled={isExempt}
          {...register("military_rank")}
          error={errors.military_rank?.message ? t(errors.military_rank.message) : undefined}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t("personalSheet.fields.militaryComposition")}
            placeholder={t("personalSheet.fields.militaryComposition")}
            disabled={isExempt}
            {...register("military_composition")}
            error={errors.military_composition?.message ? t(errors.military_composition.message) : undefined}
          />

          <Input
            label={t("personalSheet.fields.militaryBranch")}
            placeholder={t("personalSheet.fields.militaryBranch")}
            disabled={isExempt}
            {...register("military_branch")}
            error={errors.military_branch?.message ? t(errors.military_branch.message) : undefined}
          />
        </div>

        <FileUploader
          label={t("personalSheet.fields.militaryCertificate")}
          hasRequiredLabel={isMale && !isExempt}
          value={militaryFile}
          existingFileUrl={existingMilitaryFileRemoved ? null : (displayIdDocuments?.military_certificate_file_url ?? null)}
          onChange={(file) => {
            setMilitaryFile(file);
            if (file) setMilitaryFileError(null);
          }}
          onRemoveExisting={() => setExistingMilitaryFileRemoved(true)}
          error={militaryFileError ? t(militaryFileError) : undefined}
          accept=".pdf,.jpg,.jpeg,.png"
        />
      </div>
    </div>
  );
});

export default EditIdDocumentsSection;
